// Santa Clara University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: SCU moved its Common Data Set file
// (/fampf/ to /ff/); the link follows it.
//  - SCU's Common Data Set 2025-2026 (Office of Institutional Research PDF,
//    rba13-Common_Data_Set_2025-2026.pdf): C1 applicants/admits/enrollees for
//    Fall 2025 with the residency table (10 applicants and 3 admits of
//    unknown residency are in the totals only), C8 test policy for Fall 2027,
//    C21/C22 plans. Quirks: C21's early decision counts sit under the
//    template line "For the Fall 2024 entering class", but SCU's 2024-2025
//    CDS gives different Fall 2024 counts (609 applied, 488 admitted), so the
//    611 and 467 here are the Fall 2025 class; C8E gives a day (17) with no
//    month, so the score date is left out. G5 is blank; the cost comes from
//    the financial aid office.
//  - scu.edu admission pages: First-Year Students, First-Year Key Deadlines,
//    Early or Regular Decision, Test Optional. The deadlines carry no year;
//    the Test Optional page names "the 2027 term", and the CDS C8 names Fall
//    2027.
//  - SCU Financial Aid, Cost of Attendance: the 2026-2027 undergraduate
//    budget for a student living on campus. The CDS G1 lists food and housing
//    at $21,234 (a double room and the largest meal plan); the financial aid
//    budget uses $20,094.
//  - College Scorecard school page, id 122931 (net price by family income;
//    the shared DEMO_KEY was rate-limited, so the figures are the ones the
//    official page loads from the same API).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'SCU Common Data Set 2025-2026',
        url: 'https://www.scu.edu/media/offices/institutional-research/ff/common-data-set/rba13-Common_Data_Set_2025-2026.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'SCU Admission, First-Year Students',
        url: 'https://www.scu.edu/admission/undergraduate/first-year-students/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'SCU Admission, First-Year Key Deadlines',
        url: 'https://www.scu.edu/admission/undergraduate/first-year-students/fy-key-deadlines/',
        read: '2026-10-01',
    },
    earlyRegular: {
        label: 'SCU Admission, Early or Regular Decision',
        url: 'https://www.scu.edu/admission/undergraduate/first-year-students/early-or-regular-decision/',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'SCU Admission, Test Optional',
        url: 'https://www.scu.edu/admission/undergraduate/first-year-students/test-optional/',
        read: '2026-10-01',
    },
    costs: {
        label: 'SCU Financial Aid, Cost of Attendance',
        url: 'https://www.scu.edu/financialaid/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?122931-Santa-Clara-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'santa-clara-university',
    kbId: 'santa_clara_university',
    name: 'Santa Clara University',
    shortName: 'Santa Clara',
    aliases: ['SCU'],
    location: 'Santa Clara, CA',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (SCU visual identity guidelines); decoration only.
    branding: {
        primary: '#A32035',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/santa_clara_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 20032,
        admitted: 9592,
        enrolled: 1641,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10205, admitted: 4923 },
            { group: 'Out-of-state', applied: 7213, admitted: 3921 },
            { group: 'International', applied: 2604, admitted: 745 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'SCU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II', applied: 611, admitted: 467 },
        ],
        note: 'The Common Data Set gives one count for both early decision rounds together. It gives no early action counts.',
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of SCU\'s three schools: the College of Arts and Sciences, the Leavey School of Business or the School of Engineering. SCU says selectivity does not vary much between them, but your readiness for the program you choose is weighed:',
        rows: [
            { school: 'Leavey School of Business', requirement: 'Readiness at the level of calculus is expected.' },
            { school: 'School of Engineering', requirement: 'Readiness at the level of calculus is expected.' },
            { school: 'College of Arts and Sciences, natural sciences', requirement: 'Readiness at the level of calculus is expected.' },
        ],
        note: 'Once you choose a school, you can declare a major or stay undeclared.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 7' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Late December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 7', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 7', decision: 'Late March', binding: false },
        ],
        note: 'The enrollment deposit is due January 7 for Early Decision I, March 1 for Early Decision II and May 1 for Early Action and Regular Decision.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you commit to enroll in the term you applied for, and gap year requests are not considered. You may not apply early decision anywhere else. You may apply to other early action programs, but you must promptly withdraw those applications if SCU admits you. Early Action is not binding. Applicants denied in any early round are not reconsidered in Regular Decision.',
        bindingSource: 'earlyRegular',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SCU is test-optional for the 2027 term: the SAT and ACT are not required, and you choose on the Common App whether your scores are considered. If you send scores, they are read; if you do not, you are at no disadvantage in admission or merit scholarship review.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set agrees: it marks the SAT or ACT as not required for fall 2027 applicants but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'SCU has not set its test policy for fall 2028 entry yet: it says it is still reviewing the test-optional policy for future years.', source: 'testOptional' },
        ],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 64956 },
            { item: 'Student engagement and well-being fee', amount: 780 },
            { item: 'Housing and food (on campus)', amount: 20094 },
            { item: 'Books and supplies', amount: 1305 },
            { item: 'Transportation', amount: 1386 },
            { item: 'Personal and miscellaneous expenses', amount: 3969 },
            { item: 'Loan fees', amount: 66 },
        ],
        total: 92556,
        note: 'For a student living on campus. SCU\'s budget is $99,030 living off campus and $85,413 living at home.',
        source: 'costs',
        netPrice: {
            average: 50570,
            byIncome: [
                { income: '$0 to $30,000', amount: 22696 },
                { income: '$30,001 to $48,000', amount: 13570 },
                { income: '$48,001 to $75,000', amount: 25352 },
                { income: '$75,001 to $110,000', amount: 40238 },
                { income: 'Over $110,000', amount: 63129 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from SCU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
